// =============================================
// BulkAutoGeneratePanel — UI dla wyboru map + opcji bulk auto-generate
// =============================================
// STOP-F planu. Glowny komponent na stronie /mapy-tematyczne/auto-generate.
//
// Sekcje:
//  1. Header + krotki opis
//  2. Lista map (multi-select) z filtrami: "tylko gotowe (macro)" + search
//  3. Opcje pipeline'u: dropdown target stage + toggle SERP + toggle skipDone
//  4. Estymacja (proste: liczba map + jakie etapy do wykonania)
//  5. Button "Startuj batch" + AlertDialog confirm
//
// UI uproszczony vs plan v2 (PipelineBadges per mapa wycigniete do tekstu;
// LogPanel deferred). Smoke + dopracowanie w STOP-G.
// =============================================

import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Zap, ExternalLink, Info, Terminal } from "lucide-react";
import { useThematicMaps } from "@/hooks/useThematicMaps";
import { useBulkAutoGenerateContext } from "@/contexts/BulkAutoGenerateContext";
import type { TargetStage } from "@/services/autoGenerate/autoGenerateMapRunner";

const TARGET_STAGE_OPTIONS: { value: TargetStage; label: string }[] = [
  { value: "seeds", label: "Seeds (tylko wygeneruj + auto-accept)" },
  { value: "nodes", label: "Nodes (Seeds -> Nodes + auto-accept)" },
  { value: "serp", label: "SERP Validation (Seeds -> Nodes -> SERP)" },
  { value: "entities", label: "Entities (... -> Entities)" },
  { value: "eav", label: "EAV (... -> EAV)" },
  { value: "relations", label: "Relations (pelny pipeline do konca)" },
];

export function BulkAutoGeneratePanel() {
  const { maps, loading } = useThematicMaps();
  const { running, start } = useBulkAutoGenerateContext();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [onlyReady, setOnlyReady] = useState(true);
  const [targetStage, setTargetStage] = useState<TargetStage>("relations");
  const [withSerpValidation, setWithSerpValidation] = useState(true);
  const [skipDone, setSkipDone] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const filteredMaps = useMemo(() => {
    return maps.filter((m) => {
      if (onlyReady && !m.macro_statement?.trim()) return false;
      if (search && !m.name.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [maps, onlyReady, search]);

  const selectedMaps = useMemo(
    () => filteredMaps.filter((m) => selectedIds.has(m.id)),
    [filteredMaps, selectedIds],
  );

  const allFilteredSelected =
    filteredMaps.length > 0 &&
    filteredMaps.every((m) => selectedIds.has(m.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMaps.map((m) => m.id)));
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Estymacja kosztu — bardzo rough heurystyka per mapa per stage.
  const estimatedCost = useMemo(() => {
    if (selectedMaps.length === 0) return 0;
    const targetIdx = TARGET_STAGE_OPTIONS.findIndex(
      (o) => o.value === targetStage,
    );
    const stagesToRun = targetIdx + 1;
    // Approx: $0.05 LLM per stage per mapa + $0.003 per node dla SERP
    const llmCost = selectedMaps.length * stagesToRun * 0.05;
    const serpCost = withSerpValidation
      ? selectedMaps.reduce((sum, m) => sum + (m.nodes_count || 0) * 0.003, 0)
      : 0;
    return llmCost + serpCost;
  }, [selectedMaps, targetStage, withSerpValidation]);

  const handleStart = async () => {
    setConfirmOpen(false);
    if (selectedIds.size === 0) return;
    await start(Array.from(selectedIds), {
      targetStage,
      skipDone,
      withSerpValidation,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold">Auto-generuj wybrane mapy</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Wybierz mapy z wypełnionym Macro Statement i wskaż docelowy etap.
          Pipeline poleci Seeds → Nodes → SERP → Entities → EAV → Relations.
          Dane w bazie są nadpisywane tylko dla brakujących etapów.
        </p>
        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
          <Terminal className="h-3 w-3" />
          Szczegolowe logi (per stage, koszt, bledy) — klik <strong>LOGI</strong>{" "}
          w prawym dolnym rogu (badge pokazuje liczbe error'ow).
        </p>
      </div>

      {/* Filtry */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Szukaj mapy..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <div className="flex items-center gap-2">
          <Switch
            id="onlyReady"
            checked={onlyReady}
            onCheckedChange={setOnlyReady}
          />
          <Label htmlFor="onlyReady" className="text-sm">
            Tylko gotowe (macro wypełnione)
          </Label>
        </div>
        <div className="ml-auto text-sm text-muted-foreground">
          Wybrano: <strong>{selectedIds.size}</strong> z {filteredMaps.length}
        </div>
      </div>

      {/* Lista map */}
      <div className="rounded-md border max-h-[400px] overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allFilteredSelected}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead>Nazwa</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Seeds</TableHead>
              <TableHead className="text-right">Nodes</TableHead>
              <TableHead>Co wypełnione</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Ładowanie...
                </TableCell>
              </TableRow>
            ) : filteredMaps.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Brak map do wyświetlenia
                </TableCell>
              </TableRow>
            ) : (
              filteredMaps.map((m) => {
                const checked = selectedIds.has(m.id);
                const hasMacro = !!m.macro_statement?.trim();
                const hasSeeds = (m.seeds_count || 0) > 0;
                const hasNodes = (m.nodes_count || 0) > 0;
                return (
                  <TableRow
                    key={m.id}
                    className={checked ? "bg-accent/50" : ""}
                  >
                    <TableCell>
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleOne(m.id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {m.name}
                        <Link
                          to={`/mapy-tematyczne/${m.id}`}
                          target="_blank"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      </div>
                      {m.website_name && (
                        <div className="text-xs text-muted-foreground">
                          {m.website_name}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {m.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {m.seeds_count || 0}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {m.nodes_count || 0}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 text-[10px]">
                        <StageBadge label="M" filled={hasMacro} />
                        <StageBadge label="S" filled={hasSeeds} />
                        <StageBadge label="N" filled={hasNodes} />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Opcje */}
      <div className="rounded-md border p-4 space-y-4 bg-card">
        <h3 className="font-semibold text-sm">Opcje pipeline'u</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-xs">Docelowy etap</Label>
            <Select
              value={targetStage}
              onValueChange={(v) => setTargetStage(v as TargetStage)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TARGET_STAGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Reverse SERP Validation</Label>
            <div className="flex items-center gap-2 h-10">
              <Switch
                checked={withSerpValidation}
                onCheckedChange={setWithSerpValidation}
                disabled={targetStage === "seeds" || targetStage === "nodes"}
              />
              <span className="text-xs text-muted-foreground">
                {withSerpValidation ? "Wlaczone" : "Wylaczone"}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Walidacja przez DataForSEO. Wymaga skonfigurowanych kluczy.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Pomin etapy juz wykonane</Label>
            <div className="flex items-center gap-2 h-10">
              <Switch checked={skipDone} onCheckedChange={setSkipDone} />
              <span className="text-xs text-muted-foreground">
                {skipDone ? "Tak" : "Wszystko od zera"}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Drafty są auto-akceptowane bez ponownego LLM.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2 p-3 rounded-md bg-muted/50 text-[12px]">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">
            Bulk runner przetwarza mapy <strong>sekwencyjnie</strong>, jedna po
            drugiej. Wewnątrz każdej mapy pipeline używa istniejącego
            parallelism z pipeline_config (np. nodes batch parallel=3).
          </span>
        </div>
      </div>

      {/* Estymacja + Start */}
      <div className="flex items-center justify-between rounded-md border p-4 bg-card">
        <div className="text-sm">
          <div>
            <strong>{selectedIds.size}</strong> map wybranych
          </div>
          <div className="text-muted-foreground text-xs mt-1">
            Estymowany koszt LLM + SERP: ~${estimatedCost.toFixed(2)}
          </div>
        </div>
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={selectedIds.size === 0 || running}
          size="lg"
        >
          <Zap className="h-4 w-4 mr-2" />
          {running ? "Bulk w trakcie..." : "Startuj batch"}
        </Button>
      </div>

      {/* Confirm dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Rozpocząć auto-generację dla {selectedIds.size} map?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>Mapy:</strong>{" "}
              {selectedMaps
                .slice(0, 5)
                .map((m) => m.name)
                .join(", ")}
              {selectedMaps.length > 5
                ? ` ...i ${selectedMaps.length - 5} innych`
                : ""}
              <br />
              <br />
              <strong>Etap docelowy:</strong>{" "}
              {TARGET_STAGE_OPTIONS.find((o) => o.value === targetStage)?.label}
              <br />
              <strong>SERP Validation:</strong>{" "}
              {withSerpValidation ? "tak" : "nie"}
              <br />
              <strong>Pomijaj etapy wykonane:</strong>{" "}
              {skipDone ? "tak" : "nie (wszystko od zera)"}
              <br />
              <strong>Estymowany koszt:</strong> ~${estimatedCost.toFixed(2)}
              <br />
              <br />
              <span className="text-amber-600 dark:text-amber-400">
                Operacja nieodwracalna. Mapy beda generowane sekwencyjnie.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={handleStart}>
              Tak, startuj
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StageBadge({ label, filled }: { label: string; filled: boolean }) {
  return (
    <span
      className={`inline-flex items-center justify-center w-5 h-5 rounded text-[9px] font-bold ${
        filled
          ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
          : "bg-muted text-muted-foreground"
      }`}
      title={`${label} — ${filled ? "wypełnione" : "brak"}`}
    >
      {label}
    </span>
  );
}
